import React, { useState, useEffect } from 'react';
import { Badge, Image, List, Popup, Toast, Dialog } from 'antd-mobile';
import {
    ClockCircleOutlined,
    DollarOutlined,
    ExclamationCircleOutlined,
    PoweroffOutlined,
    ScanOutlined,
    SettingOutlined,
    WarningOutlined
} from '@ant-design/icons';
import {getNavBarContainerStyle} from "../../../utils/deviceUtils";
import { useNavigate } from "react-router-dom";
import {DeliveryNativePath, DspPath, UserMenuPath} from "../../../enums/DeliveryNavigatePathEnum";
import { getUserInfo, isDspManager, logout } from 'utils/auth';
import {RegisterAvatarIcon, UploadIcon} from '../../../assets/icons';
import { t } from 'i18n';
import { getAppVersion } from '../../../utils/appRuntime';
import { formatLocalizedDate } from '../../../utils/dateFormatUtils';
import { useQuery } from '@apollo/client';
import { GET_MARKER_COUNT_BY_ACCOUNT_ID_AND_TYPE, GetMarkerCountByAccountIdAndTypeResponse } from '../graphql/queries/getMarkerCount';
import {
    GET_ACCOUNT_DELIVERY_DRIVERS_BY_IDS,
    GetAccountDeliveryDriversByIdsResponse,
    GetAccountDeliveryDriversByIdsVariables
} from '../graphql/queries/getAccountDeliveryDriversByIds';
import {WEB_TEMPORARY_GRAY_ENABLED} from "../../../utils/grayscaleDriverUtils";
import {isAppVersionGreaterThanOrEqual, kNativeMapModeVersion} from "../../../utils/versionUtils";


interface UserMenuDrawerProps {
    visible: boolean;
    onClose: () => void;
    failedCount: number;
}

const UserMenuDrawer: React.FC<UserMenuDrawerProps> = ({
    visible,
    onClose,
    failedCount
}) => {
    const navigate = useNavigate();
    const [appVersion, setAppVersion] = useState<string>('V1.0');
    const [androidGoogleOptimize, setAndroidGoogleOptimize] = useState<boolean>(false);
    const userInfo = getUserInfo();
    const accountId = userInfo?.account?.id;
    const webTempGrayEnabled =  localStorage.getItem(WEB_TEMPORARY_GRAY_ENABLED) && androidGoogleOptimize;

    const { data: markerData } = useQuery<GetMarkerCountByAccountIdAndTypeResponse>(
        GET_MARKER_COUNT_BY_ACCOUNT_ID_AND_TYPE,
        {
            variables: {
                accountId: accountId,
                type: 1
            },
            skip: !accountId || !visible,
            fetchPolicy: 'cache-and-network',
            errorPolicy: 'all'
        }
    );

    const { data: driverData, loading: driverLoading } = useQuery<
        GetAccountDeliveryDriversByIdsResponse,
        GetAccountDeliveryDriversByIdsVariables
    >(GET_ACCOUNT_DELIVERY_DRIVERS_BY_IDS, {
        variables: { accountIds: accountId ? [Number(accountId)] : [] },
        skip: !accountId || !visible,
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
    });


    const markerCount = markerData?.countByAccountIdAndType || 0;

    // 获取应用版本号
    useEffect(() => {
        getAppVersion().then(version => {
            setAppVersion(version);
        }).catch(error => {
            console.error('Failed to get app version:', error);
            setAppVersion('V1.0');
        });
    }, []);

    // 检查版本是否支持Google优化
    useEffect(() => {
        isAppVersionGreaterThanOrEqual(kNativeMapModeVersion).then(result => {
            setAndroidGoogleOptimize(result);
        }).catch(error => {
            console.error('Failed to check app version:', error);
            setAndroidGoogleOptimize(false);
        });
    }, []);

    // 当前日期格式化
    const formattedDate = formatLocalizedDate(new Date(), {
        month: 'long',
        day: '2-digit',
        year: 'numeric'
    });

    const userName = userInfo?.account.name;
    const userAvatar = userInfo?.account?.avatarUrl;

    return (
        <Popup
            position='left'
            visible={visible}
            onClose={onClose}
            closeOnMaskClick
        >
            <div {...getNavBarContainerStyle('p-4 h-full flex flex-col')}>
                {/* 用户信息区域 */}
                <div className="flex items-center mb-4">
                    <div
                        className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-500 text-xl overflow-hidden">
                        {userAvatar ? (
                            <Image
                                src={userAvatar}
                                width={64}
                                height={64}
                                fit='cover'
                                style={{ borderRadius: 32 }}
                            />
                        ) : (
                            <RegisterAvatarIcon className="w-12 h-12" />
                        )}
                    </div>
                    <div className="ml-3">
                        <div className="font-medium text-base">{userName}</div>
                        <div className="text-gray-500 text-sm">{formattedDate}</div>
                    </div>
                </div>

                {/* 菜单项 */}
                <List
                    className="flex-grow"
                    style={{
                        '--border-bottom': 'none',
                        '--border-top': 'none',
                    }}>

                    <List.Item
                        prefix={<ClockCircleOutlined />}
                        onClick={() => {
                            navigate(webTempGrayEnabled ? "/delivery/delivered/temporary" : UserMenuPath.USER_MENU_DELIVERED);
                        }}
                    >
                        {t('pages.useMenu.delivered')}
                    </List.Item>
                    {webTempGrayEnabled ?
                        <List.Item
                            prefix={<UploadIcon/>}
                            extra={
                                failedCount > 0 ? (
                                    <Badge content={failedCount > 99 ? '99+' : failedCount} color="red" />
                                ) : null}
                            onClick={() => { navigate(UserMenuPath.USER_MENU_FAILED) }}
                        >
                            {t('pages.delivery.temporary.uploading.title')}
                        </List.Item>
                        :
                        <List.Item
                            prefix={<ExclamationCircleOutlined style={{ color: 'red' }} />}
                            extra={
                                failedCount > 0 ? (
                                    <Badge content={failedCount > 99 ? '99+' : failedCount} color="red" />
                                ) : null}
                            onClick={() => { navigate(UserMenuPath.USER_MENU_FAILED) }}
                        >
                            {t('pages.useMenu.failedUploads')}
                        </List.Item>
                    }
                    <List.Item
                        prefix={<ScanOutlined />}
                        onClick={() => {
                            const driverInfo = driverData?.getAccountDeliveryDriversByIds?.[0];
                            
                            // 检查司机实名状态
                            const status = driverInfo?.verificationStatus;
                            if (status !== 2) {
                                Dialog.alert({
                                    content: t('pages.useMenu.accountNotVerifiedDeliveryNotAllowed'),
                                    confirmText: t('pages.useMenu.confirm')
                                });
                                return;
                            }

                            // 检查司机离职状态
                            if (driverInfo && !driverInfo.isEmployed) {
                                Dialog.alert({
                                    content: t('pages.useMenu.accountResignedDeliveryNotAllowed'),
                                    confirmText: t('pages.useMenu.confirm')
                                });
                                return;
                            }
                            
                            navigate(UserMenuPath.USER_MENU_IDENTIFICATION);
                        }}
                        disabled={driverLoading}
                    >
                        {t('pages.useMenu.parcelIdentification')}
                    </List.Item>
                    <List.Item
                        prefix={webTempGrayEnabled ? <ExclamationCircleOutlined style={{ color: 'red' }} /> : <WarningOutlined/>}
                        extra={
                            markerCount > 0 ? (
                                <Badge content={markerCount > 99 ? '99+' : markerCount} color="red" />
                            ) : null
                        }
                        onClick={() => { navigate(DspPath.DELIVERY_DSP_DRIVER_VIOLATION_REVIEW) }}
                    >
                        {t('pages.useMenu.issueResolution')}
                    </List.Item>
                    <List.Item
                        prefix={<DollarOutlined />}
                        onClick={() => { window.location.assign(DeliveryNativePath.DRIVER_WAGE_ARREARS_REPORT) }}
                    >
                        {t('pages.useMenu.wageArrearsReport')}
                    </List.Item>
                    <List.Item prefix={<SettingOutlined />} onClick={() => { navigate(UserMenuPath.USER_MENU_SETTINGS) }}>
                        {t('pages.useMenu.settings')}
                    </List.Item>
                </List>


                {/* 底部区域 */}
                <div className="mt-auto w-full">
                    <List
                        style={{
                            '--border-top': 'none',
                        }}
                    >
                        <List.Item
                            prefix={<PoweroffOutlined />}
                            onClick={async () => { await logout('/login') }}
                        >
                            {t('pages.useMenu.signOut')}
                        </List.Item>
                    </List>
                    <div className="flex mt-2 p-2">
                        {t('pages.useMenu.privacyPolicy')}
                    </div>
                    <div className="flex p-2 text-gray-500">
                        {`V${appVersion}`}
                    </div>
                </div>
            </div>
        </Popup>
    );
};

export default UserMenuDrawer; 